import axios from "axios";
import store from "../../../Redux/store";
import {
  setListData,
  setSelectedChartOfAccount,
  setSelectedJournal,
  setSingleViewData,
} from "./payrollSlice";
import { ACTION_TYPE, VIEW_TYPE } from "./payrollEnums";
import moment from "moment-timezone";
import { clearRdxStateFunc } from "./utilFunc";
import { getCurrentDate, toUTC } from "../../../util/utilityFunctions";

//  timezone
let timeZone = moment.tz.guess();
if (timeZone === "Asia/Calcutta") {
  timeZone = "Asia/Kolkata";
}

// Centralized error handler with more detailed error information
const handleError = (error) => {
  console.error("API Error:", error);
  return {
    success: false,
    statusCode: error.response?.status || 404,
    message:
      error.response?.data?.message || error.message || "An error occurred",
  };
};

// List Journal Categories with error handling
export const listJournalCategory = async ({successCallback,errorCallback}) => {
  try {
    const response = await axios.post("account/listjournalcategory");
    if (response.status === 200) {
      store.dispatch(setSelectedJournal(response.data));
      if(successCallback){
        successCallback(response.data)
      }
      return { success: true, data: response.data };
    }
    // return { success: false, message: "Failed to fetch journal categories" };
  } catch (error) {
    if(errorCallback){
      errorCallback(error)
    }
    return handleError(error);
  }
};

// List Chart of Account Categories with error handling
export const listChartOfAccountsCategory = async () => {
  try {
    const response = await axios.post("account/listchartscategory");
    if (response.status === 200) {
      store.dispatch(setSelectedChartOfAccount(response.data));
      return { success: true };
    }
    return { success: false, message: "Failed to fetch chart of accounts" };
  } catch (error) {
    return handleError(error);
  }
};

// View configuration with error handling
export const viewConfiguration = async () => {
  try {
    const response = await axios.get("payroll/PayrollConfig");
    if (response.status === 200) {
      store.dispatch(setSingleViewData(response.data));
      return { success: true };
    }
  
  } catch (error) {
    return handleError(error);
  }
};

// initialization function
export const initializePayrollSettings = async (data) => {
  try {
    // Execute all API calls and collect their results
    const results = await Promise.all([
      listJournalCategory({ type: data.journalTypes }),
      listChartOfAccountsCategory({ type: data.journalTypes }),
      // viewConfiguration(),
    ]);

    // Check if any API call failed
    const failedCalls = results.filter((result) => !result.success);

    if (failedCalls.length > 0) {
      // Combine error messages from failed calls
      const errorMessage = failedCalls
        .map((call) => call.message)
        .filter(Boolean)
        .join(", ");

      data.setErrorMsg({
        message: errorMessage || "Failed to initialize some payroll settings",
      });
      return false;
    }

    // All calls succeeded
    data.setIsLoading(false);
    data.setErrorMsg({ message: "" });
    return true;
  } catch (error) {
    const errorResult = handleError(error);
    data.setErrorMsg({
      message: errorResult.message || "Failed to initialize payroll settings",
    });
    return false;
  } finally {
    data.setIsLoading(false);
  }
};

// Add or Update Payroll Settings with error handling
export const addPayrollSettings = async (data) => {
  try {
    const response = await axios.post("payroll/PayrollConfig", data.body);
    if (response.status === 201) {
      data.setSuccess(true);
      data.setSnackbar({
        open: true,
        message: "Configuration has been saved",
        severity: "success",
      });
      return true;
    }
    throw new Error("Failed to save configuration");
  } catch (error) {
    const errorResult = handleError(error);
    data.setSnackbar({
      open: true,
      message:
        errorResult.message || "Failed to save your configuration settings.",
      severity: "error",
    });
    return false;
  } finally {
    data.setSaveBtnLoading(false);
    setTimeout(() => {
      data.setSuccess(false);
    }, 2000);
  }
};

// add pay head api
export const addPayHeadApi = async (data) => {
  try {
    const response = await axios.post("payroll/PayHead", data.body);
    if (response.status === 200) {
      data.setSuccess(true);
      data.handleBack();
      data.setSnackbar({
        open: true,
        message: "Pay head has been added",
        severity: "success",
      });
      return response.data;
    }
    throw new Error("Failed to add pay head");
  } catch (error) {
    const errorResult = handleError(error);
    // data.setIsEdit(false)
    data.setSnackbar({
      open: true,
      message: errorResult.message || "Failed to add pay head",
      severity: "error",
    });
    return handleError(error);
  }
};

// update pay head api
export const updatePayHeadApi = async (data) => {
  try {
    const response = await axios.put("payroll/PayHead", data.body);
    if (response.status === 200) {
      data.setSuccess(true);
      data.setIsEdit(true);
      data.setSnackbar({
        open: true,
        message: "Pay head has been updated",
        severity: "success",
      });
      return response.data;
    }
    throw new Error("Failed to update pay head");
  } catch (error) {
    const errorResult = handleError(error);
    data.setIsEdit(false);
    data.setSnackbar({
      open: true,
      message: errorResult.message || "Failed to update pay head",
      severity: "error",
    });
    return handleError(error);
  }
};

// single pay head api
export const singlePayHeadApi = async (data) => {
  try {
    const response = await axios.get(`payroll/PayHead/${data.body.id}`);
    if (response.status === 200) {
      store.dispatch(setSingleViewData(response.data));
      return response.data;
    }
    throw new Error("Failed to fetch pay head");
  } catch (error) {
    store.dispatch(setSingleViewData(null));
    return handleError(error);
  }
};
// payroll payhead api's
export const payHeadInitialApi = async (data) => {
  try {
    data.setIsLoading(true);

    const results = await Promise.all([listChartOfAccountsCategory()]);

    // Check if any API call failed
    const failedCalls = results.filter((res) => !res.success);

    if (failedCalls.length > 0) {
      const errorMessage = failedCalls
        .map((call) => call.message)
        .filter(Boolean)
        .join(", ");
      data.setErrorMsg({
        message: errorMessage || "Failed to initialize some pay head API's",
      });
      return { success: false, message: errorMessage };
    }

    data.setIsLoading(false);
    return { success: true };
  } catch (error) {
    data.setIsLoading(false);
    return handleError(error);
  } finally {
    data.setIsLoading(false); // Ensure loading state is reset in all cases
  }
};

// list pay head api
export const listPayHeadApi = async (data) => {
  try {
    const response = await axios.get(
      `payroll/PayHead?page=${data?.body?.page}&limit=${
        data?.body?.limit
      }&search=${data?.body?.search}&category=${encodeURIComponent(
        data.body.category
      )}`
    );
    if (response.status === 200) {
      store.dispatch(setListData(response.data));
      if (data?.setPayHead) {
        data.setPayHead(response.data.data);
      }
      return {
        success: true,
        data: response.data,
      };
    }
    throw new Error("Failed to fetch pay head");
  } catch (error) {
    if (data?.setPayHead) {
      data.setPayHead([]);
    }
    store?.dispatch(setListData(null));
    return handleError(error);
  }
};

// list all employee

export const listAllEmployees = async (data) => {
  try {
    const response = await axios.get(
      `employee/viewAllEmployeesByBranch?branchId=${data.branchId}`
    );
    if (response.status === 200) {
      if (data?.setEmployeeList) {
        data.setEmployeeList(response.data);
      }
      if (data?.successCallback) {
        data.successCallback(response.data);
      }

      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    if (data?.setEmployeeList) {
      data.setEmployeeList([]);
    }
    if (data?.errorCallback) {
      data?.errorCallback(error);
    }
    return handleError(error);
  }
};

// save and update - variable and requiring
export const variableAndRequiringAPI = async (data) => {
  try {
    data.setLoading(true);

    const isAdd = data.actionType === ACTION_TYPE.ADD;
    const url = "payroll/variableAndRecuring";
    const method = isAdd ? axios.post : axios.put;
    const response = await method(url, data.body);

    if (response.status === 200) {
      isAdd && data.navigateTo();
      data.setIsEdit(true);
      data.setLoading(false);
      data.setSnackbar({
        open: true,
        message: isAdd
          ? "variable And Recurring has been added"
          : "variable And Recurring  been updated",
        severity: "success",
      });
    }
  } catch (error) {
    const errorResult = handleError(error);
    data.setLoading(false);
    data.setSnackbar({
      open: true,
      message:
        errorResult.message ||
        (data.actionType === ACTION_TYPE.ADD
          ? "Failed to add variable And Recurring "
          : "Failed to update variable And Recurring "),
      severity: "error",
    });
  }
};

// single view of variable and recurring
export const variableAndRecurringAPI = async (data) => {
  const viewType = data.viewType;
  data.setLoading(true);
  try {
    let url = "";
    if (viewType === VIEW_TYPE.SINGLE) {
      url = `payroll/variableAndRecuring/${data.body._id}?tz=${timeZone}`;
    } else if (viewType === VIEW_TYPE.LIST) {
      url = `payroll/variableAndRecuring?isAllowance=${data.body.isAllowance}&search=${data.body.search}&isRecurring=${data.body.isRecurring}&tz=${timeZone}&page=${data.body.page}&limit=${data.body.limit}`;
    }

    const response = await axios.get(url);

    if (response.status === 200) {
      if (viewType === VIEW_TYPE.SINGLE) {
        store.dispatch(setSingleViewData(response.data));
      } else if (viewType === VIEW_TYPE.LIST) {
        store.dispatch(setListData(response.data));
      }
      data.setLoading(false);
    }
  } catch (error) {
    // console.log("test --- >")
    if (viewType === VIEW_TYPE.SINGLE) {
      store.dispatch(setSingleViewData(null));
    } else if (viewType === VIEW_TYPE.LIST) {
      store.dispatch(setListData(null));
    }
    clearRdxStateFunc();
    const errorResult = handleError(error);
    data?.setLoading(false);
    // data?.setSnackbar({

    //   open: true,
    //   message: error.response.data || `Failed to fetch ${viewType} API`,
    //   severity: "error",
    // });
    if (typeof data?.setSnackbar === "function") {
  data.setSnackbar({
    open: true,
    message: error.response?.data || `Failed to fetch ${viewType} API`,
    severity: "error",
  });
}

  }
};

// create pay template API

export const createOrUpdatePayTemplateAPI = async (data) => {
  try {
    data.setLoading(true);
    const isAdd = data.actionType === ACTION_TYPE.ADD;
    const url = "payroll/PayTemplate";
    const method = isAdd ? axios.post : axios.put;
    const response = await method(url, data.body);
    if (response.status === 200) {
      isAdd && data.navigateTo();
      data?.setIsEdit(true);
      data?.setLoading(false);
      data?.setSnackbar({
        open: true,
        message: isAdd
          ? "pay template has been created"
          : "pay template has been updated",
        severity: "success",
      });
    }
  } catch (error) {
    const errorResult = handleError(error);
    data.setLoading(false);
    data.setSnackbar({
      open: true,
      message:
        errorResult.message ||
        (data.actionType === ACTION_TYPE.ADD
          ? "Failed to create pay template"
          : "Failed to update pay template "),
      severity: "error",
    });
  }
};

// get all pay template API
export const fetchPayTemplate = async (data) => {
  const isSingle = data.viewType === VIEW_TYPE.SINGLE;
  try {
    if (data.setLoading) {
      data.setLoading(true);
    }
    const url = isSingle
      ? `payroll/PayTemplate/${data.body._id}`
      : `payroll/PayTemplate?page=${data.body.page}&limit=${
          data.body.limit
        }&search=${data.body.search}&category=${encodeURIComponent(
          data.body.category
        )}`;

    const response = await axios.get(url);

    if (response.status === 200) {
      if (!isSingle) {
        store.dispatch(setListData(response.data));
      } else {
        store.dispatch(setSingleViewData(response.data));
      }
      if (data.setLoading) {
        data.setLoading(false);
      }
      return {
        success: true,
        data: response.data,
      };
    }

    throw new Error("Failed to fetch pay template");
  } catch (error) {
    if (data.setLoading) {
      data.setLoading(false);
    }

    if (!isSingle) {
      store?.dispatch(setListData(null));
    } else {
      store.dispatch(setSingleViewData(null));
    }
    return handleError(error);
  }
};

// get all employee branches
export const fetchEmployeeBranches = async (data) => {
  try {
    const response = await axios.post("employee/listEmployeeBranches");
    if (response.status === 200) {
      // data.setBranches(response.data);
      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    // data.setBranches([]);
    throw new Error("Failed to fetch employee branches");
  }
};

// get employee basic data API
// export const getEmployeeBasicDataAPI = async (data) => {
//   try {
//     const response = await axios.get(
//       `payroll/employeeBasicData/${
//         data.body.empId
//       }?tz=${timeZone}&postDate=${getCurrentDate()}`
//     );
//     if (response.status === 200) {
//       if (data?.successCallback) {
//         data.successCallback(response.data);
//       }

//       // data.setEmployeeData(response.data);
//     }
//   } catch (error) {
//     if (data?.errorCallback) {
//       data.errorCallback(error);
//     }
//     throw new Error("Failed to fetch employee basic data");
//     // data.setEmployeeData({});
//   }
// };
export const getEmployeeBasicDataAPI = async (data) => {
  try {
    // Optional debug logs
    console.log("Fetching data for empId:", data.body.empId);
    console.log("Time zone:", timeZone);
    console.log("Post date:", getCurrentDate());

    const response = await axios.get(
      `payroll/employeeBasicData/${data.body.empId}?tz=${timeZone}&postDate=${getCurrentDate()}`
    );

    if (response.status === 200) {
      if (data?.successCallback) {
        data.successCallback(response.data);
      }
      // Optionally set employee data here if needed
      // data.setEmployeeData?.(response.data);
    }
  } catch (error) {
    console.error("API call failed:", error);

    if (data?.errorCallback) {
      data.errorCallback(error); // Handle the error gracefully via callback
    } else {
      // Only throw if no errorCallback is provided
      throw new Error("Failed to fetch employee basic data");
    }

    // Optionally clear data if needed
    // data.setEmployeeData?.({});
  }
};


//get all departments
export const getAllDepartmentsAPI = async (data) => {
  try {
    const response = await axios.get("employee/getAllDepartments");
    if (response.status === 200) {
      // data.setDepartments(response.data);
      if (data?.successCallback) {
        data.successCallback(response.data);
      }
      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    if (data?.errorCallback) {
      data.errorCallback(error);
    }
    throw error;

    // data.setDepartments([]);
  }
};

// get all designations
export const getAllDesignationsAPI = async (data) => {
  try {
    const response = await axios.get("employee/getAllDesignations");
    if (response.status === 200) {
      if (data?.successCallback) {
        data.successCallback(response.data);
      }

      // data.setDesignations(response.data);
      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    if (data?.errorCallback) {
      data.errorCallback(error);
    }
    // data.setDesignations([]);
  }
};

// save payslip API
export const savePayslipAPI = async (data) => {
  try {
    const response = await axios.post("payroll/PaySlip", data.body);
    if (response.status === 200) {
      data.successCallback(response.data);
      //save single view id in local storage
      localStorage.setItem("singleViewId", response.data._id);

      return response.data;
    }
  } catch (error) {
    const errorResult = handleError(error);
    data.errorCallback(errorResult);
    return handleError(error);
  }
};

// single payslip API
export const singlePayslipAPI = async (data) => {
  try {
    const response = await axios.get(
      `payroll/paySlip/${data.body._id}?tz=${timeZone}`
    );
    if (response.status === 200) {
      data.successCallback(response.data[0]);
      //save single view id in local storage
      localStorage.setItem("singleViewId", response.data._id);
      return response.data;
    }
  } catch (error) {
    data.errorCallback(error);
    return handleError(error);
  }
};

//get all branches api
export const getAllBranchesAPI = async ({ successCallback, errorCallback }) => {
  try {
    const response = await axios.post("admin/viewbranches");
    if (response.status === 200) {
      if (successCallback) {
        successCallback(response.data);
      }
      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    const errorResult = handleError(error);
    errorCallback(errorResult);
    return handleError(error);
  }
};

// fetch all payslip API

export const fetchAllPayslipAPI = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  try {
    // Construct query parameters
    const params = Object.entries(body)
      .map(([key, value]) =>
        Array.isArray(value) ? `${key}=${value.join(",")}` : `${key}=${value}`
      )
      .join("&");

    const response = await axios.get(`payroll/paySlip?${params}`);

    if (response.status === 200) {
      successCallback(response.data);
      return response.data;
    }
  } catch (error) {
    errorCallback(error);
    return handleError(error);
  }
};
//update payslip api
export const updatePayslipAPI = async (data) => {
  try {
    const response = await axios.put("payroll/PaySlip", data.body);
    if (response.status === 200) {
      data.successCallback(response.data);
      //save single view id in local storage
      // localStorage.setItem("singleViewId", response.data._id);

      return response.data;
    }
  } catch (error) {
    const errorResult = handleError(error);
    data.errorCallback(errorResult);
    return handleError(error);
  }
};

//post  payslip api
export const postPayslipAPI = async (data) => {
  try {
    const response = await axios.post("payroll/postPaySlip", {
      ...data.body,
      tz: timeZone,
    });
    if (response.status === 200) {
      data.successCallback(response.data);
      //save single view id in local storage
      localStorage.setItem("singleViewId", response.data._id);

      return response.data;
    }
  } catch (error) {
    const errorResult = handleError(error);
    data.errorCallback(errorResult);
    return handleError(error);
  }
};

//get all department api
export const departmentListApi = async ({ successCallback, errorCallback }) => {
  try {
    const response = await axios.get("employee/getAllDepartments");
    if (response.status === 200) {
      successCallback(response.data);
      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    const errorResult = handleError(error);
    errorCallback(errorResult);
    return handleError(error);
  }
};

// =================== batch payslip =================================
// Unified API call function
const apiCall = async ({
  endpoint,
  method,
  body,
  successCallback,
  errorCallback,
}) => {
  try {
    const config = {
      method: method.toLowerCase(),
      url: endpoint,
    };

    if (body && method.toUpperCase() !== "GET") {
      // Allow sending body for DELETE and other non-GET methods
      config.data = body;
    }

    const response = await axios(config);

    if (response.status >= 200 && response.status < 300) {
      successCallback(response.data);
      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    const errorResult = handleError(error);
    errorCallback(errorResult);
    return errorResult;
  }
};

// Specific API functions using the unified apiCall
export const saveBatchPayslip = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  const apiBody = {
    batchName: body.batchName,
    fromDate: toUTC(body.fromDate),
    toDate: toUTC(body.toDate),
    branchId: body.branch?._id || body.branch,
    department: body.department?._id || body.department,
  };
  return apiCall({
    endpoint: "payroll/batchPaySlip",
    method: "POST",
    body: apiBody,
    successCallback,
    errorCallback,
  });
};

export const computeBatchPayslip = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  const apiBody = {
    id: body._id,
    postDate: new Date().toISOString(),
  };

  return apiCall({
    endpoint: "payroll/computeBatchPaySlip",
    method: "POST",
    body: apiBody,
    successCallback,
    errorCallback,
  });
};

export const updateBatchPayslip = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  const apiBody = {
    id: body._id,  // Backend expects 'id' not '_id'
    batchName: body.batchName,
    fromDate: body.fromDate,
    toDate: body.toDate,
    branchId: body.branch?._id || body.branch,
    department: body.department?._id || body.department,
  };

  return apiCall({
    endpoint: "payroll/editBatchPaySlip",
    method: "POST",
    body: apiBody,
    successCallback,
    errorCallback,
  });
};

export const postBatchPayslip = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  const apiBody = {
    batchId: body._id,
    postDate: new Date().toISOString(),
  };

  return apiCall({
    endpoint: "payroll/postBatchPaySlip",
    method: "POST",
    body: apiBody,
    successCallback,
    errorCallback,
  });
};

export const batchPayslipSingleView = async ({
  id,
  // timeZone
  successCallback,
  errorCallback,
}) => {
  return apiCall({
    endpoint: `payroll/batchPaySlip/${id}?tz=${timeZone}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

//get all batch payslip list
export const getBatchPayslipList = async ({
  page = 1,
  limit = 10,
  search = "",
  branchId = "",
  department = "",
  status = "",
  fromDate = "",
  toDate = "",
  tz = timeZone,
  successCallback,
  errorCallback,
}) => {
  const toCommaSeparated = (value) => {
    if (Array.isArray(value)) {
      return value.join(",");
    }
    return value;
  };

  // Construct query parameters dynamically
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(search && { search }),
    ...(branchId && { branchId: toCommaSeparated(branchId) }),
    ...(department && { department: toCommaSeparated(department) }),
    ...(status && { status: toCommaSeparated(status) }),
    ...(fromDate && { fromDate }),
    ...(toDate && { toDate }),
    tz,
  }).toString();

  return apiCall({
    endpoint: `payroll/batchPaySlip?${queryParams}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

// delete individual payslip
export const deleteBatchPayslip = async ({
  id,
  successCallback,
  errorCallback,
}) => {
  return apiCall({
    endpoint: "payroll/deletePaySlip", // Individual payslip delete endpoint
    method: "POST",
    body: { id }, // Send id in body for POST
    successCallback,
    errorCallback,
  });
};
// ======================Salary Disbursement=========================

export const getPaySlipList = async ({
  branch,
  department,
  successCallback,
  errorCallback,
}) => {
  return apiCall({
    endpoint: `payroll/paySlipListForsalaryDisbursment?branchId=${branch}&department=${department}&tz=${timeZone}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

//save disbursement
export const saveDisbursement = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  const apiBody = {
    paymentMethod:body.paymentMethod?._id || "",
    accountingDate: toUTC(body.accountingDate),
    paymentType: body.paymentType||"",
    referance: body.reference||"",
    chequeNo: body.chequeOrTxnNo||"",
    txnDate: toUTC(body.transactionDate),
    branchId:  body.branch?._id || body.branch,
    department: body.department?._id || body.department,
    paySlipList: Array.isArray(body?.payslipList) ? body.payslipList.map(({ _id }) => _id) : [],
  };
  return apiCall({
    endpoint: "payroll/salaryDisbursment",
    method: "POST",
    body: apiBody,
    successCallback,
    errorCallback,
  });
};

//SINGLE VIEW 
export const salaryDisbursementSingleView = async ({
  id,
  successCallback,
  errorCallback,
}) => {
  return apiCall({
    endpoint: `payroll/salaryDisbursment/${id}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

// SALARY DISBURSEMENT 
export const listSalaryDisbursemenT = async ({
  id,
  successCallback,
  errorCallback,
}) => {
  return apiCall({
    endpoint: `payroll/salaryDisbursment/${id}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

// UPDATE SALARY DISBURSEMENT 
export const updateSalaryDisbursement = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  const apiBody = {
    paymentMethod:body.paymentMethod?._id || "",
    accountingDate: toUTC(body.accountingDate),
    paymentType: body.paymentType||"",
    referance: body.reference||"",
    chequeNo: body.chequeOrTxnNo||"",
    txnDate: toUTC(body.transactionDate),
    branchId:  body.branch?._id || body.branch,
    department: body.department?._id || body.department,
    paySlipList: body?.payslipList?.map(({ _id }) => _id)||[],
    name:body?.name||"",
    _id:body?._id||null,
  };

  return apiCall({
    endpoint: "payroll/salaryDisbursment",
    method: "PUT",
    body: apiBody,
    successCallback,
    errorCallback,
  });
};

// POST DISBURSEMENT 
export const postDisbursement = async ({
  body,
  successCallback,
  errorCallback,
}) => {
  const apiBody = {
    id: body._id,
    postDate: new Date().toISOString(),
  };

  return apiCall({
    endpoint: "payroll/postSalaryDisbursment",
    method: "POST",
    body: apiBody,
    successCallback,
    errorCallback,
  });
};

//LIST SALARY DISBURSEMENT 
export const getListOfSalaryDisbursement = async ({
  page = 1,
  limit = 10,
  branchId = "",
  paymentMethod = "",
  status = "",
  paymentType = "",
  fromDate = "",
  toDate = "",
  search,
  successCallback,
  errorCallback,
}) => {

  const toCommaSeparated = (value) => {
    if (Array.isArray(value)) {
      return value.join(",");
    }
    return value;
  };

  // Construct query parameters dynamically
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(branchId && { branchId: toCommaSeparated(branchId) }),
    ...(paymentMethod && { paymentMethod: toCommaSeparated(paymentMethod) }),
    ...(status && { status: toCommaSeparated(status) }),
    ...(paymentType && { paymentType: toCommaSeparated(paymentType) }),
    ...(fromDate && { fromDate }),
    ...(toDate && { toDate }),
    search,
  }).toString();
  
  return apiCall({
    endpoint: `payroll/salaryDisbursment?${queryParams}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

// ------------------------------end-------------------
//  get emp amount variable / recurring 
export const VandRAmtForEmployee = async ({
  empId,
  payHeadId,
  successCallback,
  errorCallback,
}) => {
  return apiCall({
    endpoint: `payroll/variableAndRecurringAmountForEmp?empId=${empId}&payheadId=${payHeadId}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};


// =================== EOSS (End of Service Settlement) APIs ===================
// Added on 01-11-2025 - Modern implementation with best practices

/**
 * Generate sequence number for new EOSS record
 */
export const generateEOSSSequence = async ({ currentDate, successCallback, errorCallback }) => {
  return apiCall({
    endpoint: "payroll/eoss/generate-sequence",
    method: "POST",
    body: { currentDate },
    successCallback,
    errorCallback,
  });
};

/**
 * Create new EOSS record
 */
export const createEOSS = async ({ body, successCallback, errorCallback }) => {
  return apiCall({
    endpoint: "payroll/eoss",
    method: "POST",
    body,
    successCallback,
    errorCallback,
  });
};

/**
 * Update existing EOSS record
 */
export const updateEOSS = async ({ body, successCallback, errorCallback }) => {
  return apiCall({
    endpoint: "payroll/eoss",
    method: "PUT",
    body,
    successCallback,
    errorCallback,
  });
};

/**
 * Get EOSS list with pagination and filtering
 */
export const getEOSSList = async ({
  page = 1,
  limit = 10,
  search = "",
  status = "",
  empId = "",
  successCallback,
  errorCallback,
}) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(search && { search }),
    ...(status && { status }),
    ...(empId && { empId }),
    _ts: Date.now().toString(), // cache-busting to avoid interceptor cache
  }).toString();

  return apiCall({
    endpoint: `payroll/eoss?${queryParams}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

/**
 * Get single EOSS record by ID
 */
export const getEOSSById = async ({ id, successCallback, errorCallback }) => {
  return apiCall({
    endpoint: `payroll/eoss/${id}`,
    method: "GET",
    successCallback,
    errorCallback,
  });
};

/**
 * Delete EOSS record (only for Draft status)
 */
export const deleteEOSS = async ({ body, successCallback, errorCallback }) => {
  return apiCall({
    endpoint: `payroll/eoss`,
    method: "DELETE",
    body,
    successCallback,
    errorCallback,
  });
};

/**
 * Post EOSS record (change status from Draft to Posted)
 */
export const postEOSS = async ({ body, successCallback, errorCallback }) => {
  return apiCall({
    endpoint: `payroll/eoss/post`,
    method: "POST",
    body,
    successCallback,
    errorCallback,
  });
};

// fetch multiple apis
export const fetchMultipleAPIs = async (prop) => {
  try {
    prop.setLoading(true);

    // Extract API call promises
    const apiCalls = prop.apiCallsWithNames.map((item) => item.apiCall);
    const results = await Promise.all(apiCalls);

    // Identify failed API calls
    const failedCalls = results.filter((res) => !res.success);
    if (failedCalls.length > 0) {
      const errorMessage = failedCalls
        .map((call) => call.message)
        .filter(Boolean)
        .join(", ");
      prop.setErrorMsg({
        message: errorMessage || "Failed to initialize some settings",
      });
      prop.onError({ success: false, message: errorMessage });
      return { success: false, message: errorMessage };
    }

    // Normalize response data to ensure a consistent structure
    const responseData = {};
    prop.apiCallsWithNames.forEach((item, index) => {
      responseData[item.name] =
        results[index]?.data?.data || results[index]?.data || [];
    });

    prop.setLoading(false);
    prop.onSuccess({ success: true, data: responseData });
    return { success: true, data: responseData };
  } catch (error) {
    prop.setLoading(false);
    const errorResponse = handleError(error);
    prop.onError(errorResponse);
    return errorResponse;
  } finally {
    prop.setLoading(false);
  }
};
